import { Component } from 'react';
import ErrorFallback from './ui/ErrorFallback';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Auto-reload on stale chunk errors (happens after deployments)
    const isChunkError = error?.message?.includes('dynamically imported module') ||
                         error?.message?.includes('Importing a module script failed') || // Safari wording
                         error?.message?.includes('Failed to fetch') ||
                         error?.message?.includes('Loading chunk') ||
                         error?.message?.includes('Loading CSS chunk');
    // Loop guard is time-based: allow another auto-reload if the last one was
    // more than 30s ago (a long-lived tab can span multiple deploys), but never
    // reload twice in quick succession (a truly broken chunk shows the fallback).
    const reloadKey = 'evv_chunk_reload_at';
    const lastReloadAt = parseInt(sessionStorage.getItem(reloadKey) || '0', 10);
    if (isChunkError && Date.now() - lastReloadAt > 30000) {
      sessionStorage.setItem(reloadKey, String(Date.now()));
      console.log('🔄 Stale chunk detected — auto-reloading...');
      window.location.reload();
      return;
    }

    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" aria-live="assertive">
          <ErrorFallback
            error={this.state.error}
            resetError={() => {
              this.setState({ hasError: false, error: null, errorInfo: null });
              if (this.props.onRetry) {
                this.props.onRetry();
              }
            }}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
